import { NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivityLocation, IBoondock } from '@curbnturf/entities';
import { CardsAcceptedComponent, DisplayValidationErrorsDirective } from '@curbnturf/form-helpers';
import { ScheduleFacade } from '@curbnturf/schedule/src/lib/+state/schedule.facade';
import { LoadingController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { CreditCardEditingComponent } from '../credit-card-editing/credit-card-editing.component';
import { DonationService } from '../donation.service';

@Component({
  selector: 'curbnturf-donation',
  templateUrl: './donation.component.html',
  styleUrls: ['./donation.component.scss'],
  standalone: true,
  imports: [
    CardsAcceptedComponent,
    CreditCardEditingComponent,
    DisplayValidationErrorsDirective,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class DonationComponent implements OnInit, OnDestroy {
  @Input('confirmationCode')
  confirmationCode?: string;

  @Input() boondockSite: IBoondock;

  @Input() poi: ActivityLocation;

  @Output() makeDonation: EventEmitter<void> = new EventEmitter<void>();

  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('amountEl') amountElement: ElementRef;

  @ViewChild('creditCardEditing')
  creditCardEditingComponent: CreditCardEditingComponent;

  subscriptions: Subscription[] = [];

  donationForm: FormGroup<{ amount: FormControl<number | null> }>;
  customAmount: boolean = false;

  get donationAmount() {
    return this.donationForm.value.amount;
  }

  constructor(
    private fb: FormBuilder,
    private donationService: DonationService,
    private toastController: ToastController,
    private loadingController: LoadingController,
    private scheduleFacade: ScheduleFacade,
  ) {}

  ngOnInit() {
    if (!this.boondockSite && this.confirmationCode) {
      this.subscriptions.push(
        this.scheduleFacade.selected$.subscribe((schedule) => {
          if (schedule?.boondock) {
            this.boondockSite = schedule.boondock;
          }
        }),
      );
    }

    this.donationForm = this.fb.group({
      amount: 5,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onMakeDonation(event: MouseEvent) {
    event.preventDefault();

    this.creditCardEditingComponent.updateFormValidity();
    if (
      (!this.creditCardEditingComponent.ccForm.valid || !this.creditCardEditingComponent.addressForm.valid) &&
      !this.creditCardEditingComponent.isCardSaved
    ) {
      return;
    }

    const amount = this.donationForm.value.amount;

    if (!amount) {
      const toast = await this.toastController.create({
        message: 'Please select an amount before donating!',
        duration: 10000,
      });
      await toast.present();
      return;
    }

    const loading = await this.loadingController.create({
      message: 'Processing Your Donation',
      duration: 30000,
    });
    await loading.present();

    this.subscriptions.push(
      this.creditCardEditingComponent.saveCreditCard().subscribe({
        next: () => {
          if (this.boondockSite?.id) {
            this.donationService.makeDonationBoondock(this.boondockSite?.id, amount, this.confirmationCode).subscribe({
              next: async () => {
                if (this.confirmationCode) {
                  this.scheduleFacade.load(this.confirmationCode);
                }
                const toast = await this.toastController.create({
                  message: 'Thank you for your donation!',
                  duration: 2000,
                });
                await toast.present();
              },
              error: async () => {
                const toast = await this.toastController.create({
                  message: 'An error occurred while processing your donation.',
                  duration: 2000,
                  color: 'warning',
                });
                await loading.dismiss();
                await toast.present();
              },
              complete: async () => {
                await loading.dismiss();
                this.close.emit();
                this.makeDonation.emit();
              },
            });
          } else if (this.poi && this.poi.id) {
            this.donationService.makeDonationPoi(this.poi?.id, amount).subscribe({
              next: async () => {
                const toast = await this.toastController.create({
                  message: 'Thank you for your donation!',
                  duration: 2000,
                });
                await toast.present();
              },
              error: async () => {
                const toast = await this.toastController.create({
                  message: 'An error occurred while processing your donation.',
                  duration: 2000,
                  color: 'warning',
                });
                await loading.dismiss();
                await toast.present();
              },
              complete: async () => {
                await loading.dismiss();
                this.close.emit();
                this.makeDonation.emit();
              },
            });
          }
        },
        error: async (e) => {
          await this.toastController.create({
            message: `An error occurred while processing your donation. ${e.message && 'Error: ' + e.message}`,
            duration: 2000,
            color: 'warning',
          });
          loading.dismiss();
        },
        complete: () => {
          loading.dismiss();
        },
      }),
    );
  }

  setAmount(amount: number) {
    if (amount < 5) {
      this.customAmount = true;
      this.donationForm.patchValue({ amount: undefined });
      setTimeout(() => {
        this.amountElement.nativeElement.focus();
      });
    } else {
      this.customAmount = false;
      this.donationForm.patchValue({ amount });
    }
  }
}
